import React, { useState } from "react";
import { MapPin, Phone, Mail, Clock, MessageCircle, Send, Wrench, Scale, CreditCard, Gem } from "lucide-react";
import { STORE, IMAGES, BRAND, saveInquiry, waLink } from "../lib/store";
import { useEdit } from "../lib/content";
import { E, EImg } from "../components/edit/EditKit";
import Seo, { jewelryStoreSchema, faqSchema, breadcrumbSchema } from "../components/Seo";
import { trackInquiry, trackContactClick } from "../lib/analytics";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

const SERVICES = [
  { icon: Gem, title: "Custom Jewelry", desc: "Design custom gold and diamond pieces with our jewelers — from engagement rings to bridal sets." },
  { icon: Wrench, title: "Jewelry Repair", desc: "Chain repairs, prong tightening, resizing and restoration for gold and diamond jewelry." },
  { icon: Scale, title: "Appraisals", desc: "Professional jewelry appraisals for insurance and estate purposes." },
  { icon: CreditCard, title: "Financing", desc: "Flexible financing options available in store — ask our team for current plans." },
];

const FAQS = [
  { q: "Where is Regal Jewels located?", a: "We are located at 2625 W. Devon Ave., Chicago, IL 60659, in the heart of Devon Avenue's famous jewelry district in West Ridge, Chicago. Street parking is available along Devon Avenue and nearby side streets." },
  { q: "What are your store hours?", a: "We are open 7 days a week, 11:00 AM to 8:00 PM. Walk-ins and private consultations are welcome daily." },
  { q: "Do you ship jewelry across the USA?", a: "Yes. We offer fully insured delivery across the United States. You can inquire about any piece online or on WhatsApp at 773-517-0574 and we will confirm details and shipping." },
  { q: "What types of jewelry do you carry?", a: "We carry 18k, 22k and 24k gold jewelry, certified diamond jewelry, silver, MALO wedding bands, Lladró porcelain and gold and silver bullion — over 1,300 pieces including bridal sets, bangles, chains, mangalsutras and more." },
  { q: "Do you offer jewelry repair and appraisals?", a: "Yes. We offer jewelry repair, professional appraisals, custom jewelry design and financing options at our Chicago store." },
  { q: "Can I book a private appointment?", a: "Absolutely. You can book a private consultation online through our booking page, or message us on WhatsApp, and try on pieces at our Devon Avenue showroom." },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const { get } = useEdit();
  const phone = get("store.phone", STORE.phone);
  const whatsapp = get("store.whatsapp", STORE.whatsapp);
  const emailAddr = get("store.email", STORE.email);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return toast.error("Please fill in name, email and message");
    try {
      await saveInquiry({ ...form, product: null, source: "contact-page" });
      trackInquiry("contact-page");
      toast.success("Message sent! We'll reply within 24 hours.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error("Could not send message. Please check your email and try again.");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-12">
      <Seo
        title="Jewelry Store in Chicago — Visit or Contact Us"
        description="Visit Regal Jewels at 2625 W. Devon Ave, Chicago — open 7 days, 11 AM–8 PM. Custom jewelry, repair, appraisals & financing. Call 773-262-4377 or WhatsApp us."
        path="/contact"
        jsonLd={[
          jewelryStoreSchema(),
          faqSchema(FAQS),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Contact", path: "/contact" },
          ]),
        ]}
      />
      <div className="text-center mb-14">
        <p className="section-eyebrow">Devon Avenue, Chicago</p>
        <E as="h1" id="contact.heading" default="Visit Our Jewelry Store in Chicago" className="section-title" />
        <E as="p" id="contact.intro" multiline
          default={"Regal Jewels is a family jewelry store on Devon Avenue — Chicago's historic gold district in West Ridge.\nBrowse 22K gold, diamonds and silver in person, or reach us by phone, email or WhatsApp from anywhere in the USA."}
          className="text-[#5c5240] mt-5 max-w-2xl mx-auto leading-relaxed" />
      </div>

      {/* About band */}
      <div className="grid lg:grid-cols-2 gap-10 items-center mb-20">
        <EImg id="contact.storefront" default={BRAND.storefront} alt="Regal Jewels storefront in Little India, Devon Avenue Chicago" className="w-full h-96 object-cover object-center border border-[#eee6d6]" wrapperClassName="w-full" />
        <div>
          <E as="h2" id="contact.aboutHeading" default="About Regal Jewels" className="font-heading text-2xl sm:text-3xl text-[#171310]" />
          <E as="p" id="contact.aboutP1" multiline
            default={"Located in the heart of Chicago's historic Devon Avenue gold district, Regal Jewels offers an extensive collection of 18k, 22k and 24k gold jewelry, certified diamonds, silver, and fine bullion. From traditional bridal sets, bangles and mangalsutras to contemporary designs and MALO wedding bands, every piece reflects purity and craftsmanship you can trust."}
            className="text-[#5c5240] leading-relaxed mt-5" />
          <E as="p" id="contact.aboutP2" multiline
            default="Shop in-store, inquire online, or chat with us on WhatsApp — with insured delivery across the USA."
            className="text-[#5c5240] leading-relaxed mt-4" />
          <EImg
            id="contact.oneStopShop"
            default={BRAND.oneStopShop}
            alt="Regal Jewels — one stop shop for the entire family, from baby to grandma. Come on in!"
            className="w-full h-auto mt-8 border border-[#eee6d6]"
            wrapperClassName="block mt-8"
            loading="lazy"
            data-testid="one-stop-shop-banner"
          />
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-10">
        {/* Contact form */}
        <form onSubmit={submit} className="bg-white border border-[#eee6d6] p-6 sm:p-10">
          <h2 className="font-heading text-2xl text-[#171310] mb-7">Send an Inquiry</h2>
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-[#7a7160]">Name *</Label>
              <Input value={form.name} onChange={set("name")} className="rounded-none border-[#d8cdb5] h-11" placeholder="Your name" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-[#7a7160]">Phone</Label>
              <Input value={form.phone} onChange={set("phone")} className="rounded-none border-[#d8cdb5] h-11" placeholder="(773) 000-0000" />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs uppercase tracking-wider text-[#7a7160]">Email *</Label>
              <Input type="email" value={form.email} onChange={set("email")} className="rounded-none border-[#d8cdb5] h-11" placeholder="you@email.com" />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs uppercase tracking-wider text-[#7a7160]">Message *</Label>
              <Textarea value={form.message} onChange={set("message")} rows={5} className="rounded-none border-[#d8cdb5]" placeholder="Ask about a piece, custom orders, gold rates, repairs..." />
            </div>
          </div>
          <Button type="submit" className="w-full mt-7 rounded-none bg-[#171310] hover:bg-[#3a2f1d] text-[#e9d9ac] tracking-[0.15em] uppercase text-xs h-12 transition-colors duration-300">
            <Send size={15} className="mr-2" /> Send Message
          </Button>
        </form>

        {/* Info */}
        <div className="space-y-6">
          <div className="bg-[#faf7f0] border border-[#ece5d5] p-8 sm:p-10 text-[#5c5240]">
            <h3 className="font-heading text-2xl text-[#1a1713] mb-6">Store Details</h3>
            <ul className="space-y-5 text-sm">
              <li className="flex gap-3"><MapPin size={18} className="text-[#a8842c] shrink-0 mt-0.5" /> <E id="store.address" default={STORE.address} /></li>
              <li className="flex gap-3"><Phone size={18} className="text-[#a8842c] shrink-0 mt-0.5" />
                <span>
                  <a href={`tel:${phone}`} onClick={() => trackContactClick("phone")} className="hover:text-[#a8842c] transition-colors"><E id="store.phone" default={STORE.phone} /></a>
                  <br /><span className="text-[#8f8571]">WhatsApp: <E id="store.whatsapp" default={STORE.whatsapp} /></span>
                </span>
              </li>
              <li className="flex gap-3"><Mail size={18} className="text-[#a8842c] shrink-0 mt-0.5" />
                <a href={`mailto:${emailAddr}`} onClick={() => trackContactClick("email")} className="hover:text-[#a8842c] transition-colors"><E id="store.email" default={STORE.email} /></a>
              </li>
              <li className="flex gap-3"><Clock size={18} className="text-[#a8842c] shrink-0 mt-0.5" />
                <E id="store.hoursText" multiline default={STORE.hours.map((h) => `${h.d}: ${h.h}`).join("\n")} />
              </li>
            </ul>
            <a href={waLink("Hello Regal Jewels! I have a question.")} target="_blank" rel="noopener noreferrer">
              <Button className="w-full mt-8 rounded-none bg-[#25D366] hover:bg-[#1fb457] text-white tracking-[0.12em] uppercase text-xs h-12 transition-colors duration-300">
                <MessageCircle size={15} className="mr-2" /> Chat on WhatsApp
              </Button>
            </a>
          </div>

          {/* Map */}
          <div className="border border-[#eee6d6] overflow-hidden">
            <iframe
              title="Regal Jewels location"
              src="https://www.google.com/maps?q=2625+W+Devon+Ave,+Chicago,+IL+60659&output=embed"
              className="w-full h-72 border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </div>

      {/* Services */}
      <section className="pt-20">
        <div className="text-center mb-12">
          <p className="section-eyebrow">In-Store Services</p>
          <h2 className="section-title">More Than a Jewelry Store</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {SERVICES.map(({ icon: Icon, title, desc }) => (
            <div key={title} className="bg-white border border-[#eee6d6] p-7">
              <Icon size={22} strokeWidth={1.3} className="text-[#a8842c]" />
              <h3 className="font-heading text-xl text-[#1a1713] mt-4">{title}</h3>
              <p className="text-sm text-[#5c5240] leading-relaxed mt-2">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQs */}
      <section className="pt-20 max-w-3xl mx-auto">
        <div className="text-center mb-10">
          <p className="section-eyebrow">Good to Know</p>
          <h2 className="section-title">Frequently Asked Questions</h2>
        </div>
        <div className="divide-y divide-[#eae2d0] border-y border-[#eae2d0]">
          {FAQS.map((f) => (
            <details key={f.q} className="group py-5">
              <summary className="font-heading text-lg sm:text-xl text-[#1a1713] cursor-pointer list-none flex justify-between items-center gap-4">
                {f.q}
                <span className="text-[#a8842c] text-2xl leading-none group-open:rotate-45 transition-transform duration-300">+</span>
              </summary>
              <p className="text-sm text-[#5c5240] leading-relaxed mt-3 pr-8">{f.a}</p>
            </details>
          ))}
        </div>
      </section>
    </div>
  );
}
